import * as React from 'react'

import OptionsFooter from '~/client/src/shared/components/OptionsList/pieces/OptionsFooter'
import OptionsSeparator from '~/client/src/shared/components/OptionsList/pieces/OptionsSeparator'
import { SHEET_INSET_CLASS } from '~/client/src/shared/components/OptionsList/surfaceInset'
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from '~/client/src/shared/components/shadcn-ui/drawer'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { IViewOptionsPanelProps } from './types'
import useViewOptionsDraft from './useViewOptionsDraft'
import ViewOptionsPanel from './ViewOptionsPanel'

interface IProps extends IViewOptionsPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger: React.ReactNode
}

// Staged view options: edits accumulate in a draft and only reach the domain on
// Apply. Mounted only while the drawer is open, so the draft reseeds from the
// live values every time the sheet reopens.
const StagedViewOptions: React.FC<
  IViewOptionsPanelProps & { onClose: () => void }
> = ({ onClose, ...sections }) => {
  const { panelProps, commit } = useViewOptionsDraft(sections)

  const handleApply = React.useCallback(() => {
    commit()
    onClose()
  }, [commit, onClose])

  return (
    <div className="tw:flex tw:min-h-0 tw:flex-col">
      <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
        <ViewOptionsPanel {...panelProps} onClose={onClose} />
      </div>
      <OptionsSeparator className="tw:my-2" />
      <OptionsFooter
        onCancel={onClose}
        onApply={handleApply}
        size="lg"
        layout="fill"
      />
    </div>
  )
}

const ViewOptionsSheet: React.FC<IProps> = ({
  open,
  onOpenChange,
  trigger,
  grouping,
  sorting,
  columns,
  autoFocusSearch,
}) => (
  // noBodyStyles: nested inside another vaul drawer, closing this one would
  // restore <body> while the parent is still open (vaul keeps one global slot).
  <Drawer open={open} onOpenChange={onOpenChange} noBodyStyles>
    <DrawerTrigger asChild>{trigger}</DrawerTrigger>
    <DrawerContent className={SHEET_INSET_CLASS} aria-describedby={undefined}>
      <DrawerTitle className="tw:sr-only">
        {`${Localization.translator.view_noun} ${Localization.translator.options}`}
      </DrawerTitle>
      <StagedViewOptions
        grouping={grouping}
        sorting={sorting}
        columns={columns}
        autoFocusSearch={autoFocusSearch}
        onClose={() => onOpenChange(false)}
      />
    </DrawerContent>
  </Drawer>
)

export default ViewOptionsSheet
